import { Injectable } from '@angular/core';
import { AlertMessage, AlertMessageService } from '@sigepe/sigepe-template';
import { MessageService as PrimeMessageService } from 'primeng/api';
import DestroyMixin from './destroy-mixin';
import { takeUntil } from 'rxjs/operators';

const MIN_DISPLAY_TIME = 4000; //em millis

type Severity = 'error' | 'warn' | 'success';

interface CustomOptions {
    keepOnNavigation: boolean;
    time: number;
}

function addCustomOptions(msg: AlertMessage, keepOnNavigation: boolean): AlertMessage {
    const options: CustomOptions = msg.options as CustomOptions;
    options.keepOnNavigation = keepOnNavigation;
    options.time = Date.now();
    return msg;
}

@Injectable({
    providedIn: 'root'
})
export class MessageService extends DestroyMixin(Object) {
    private _messages: AlertMessage[] = [];

    constructor(
        private primeMessageService: PrimeMessageService,
        private alertService: AlertMessageService
    ) {
        super();
        alertService
            .getMessages$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => (this._messages = value));
    }

    get messages(): AlertMessage[] {
        return this._messages;
    }

    set messages(value: AlertMessage[]) {
        this._messages = value || [];
    }

    error(message: string, keepOnNavigation = false): void {
        addCustomOptions(this.alertService.error(message), keepOnNavigation);
    }

    success(message: string, keepOnNavigation = false): void {
        addCustomOptions(this.alertService.success(message), keepOnNavigation);
    }

    warn(message: string, keepOnNavigation = false): void {
        addCustomOptions(this.alertService.warn(message), keepOnNavigation);
    }

    toast(detail: string, severity: Severity = 'error'): void {
        this.primeMessageService.add({
            severity: severity,
            detail: detail,
            key: 'toast'
        });
    }

    removeOld(): void {
        this.filter(MessageService.isRecent);
    }

    private static isRecent(msg: AlertMessage): boolean {
        const options: CustomOptions = msg.options as CustomOptions;
        return options.time && Date.now() - options.time < MIN_DISPLAY_TIME;
    }

    removeOnNavigation(): void {
        this.filter(msg => {
            const options: CustomOptions = msg.options as CustomOptions;
            if (options.keepOnNavigation) {
                options.keepOnNavigation = false;
                return true;
            }
            return false;
        });
    }

    private filter(filterFn: (msg: AlertMessage) => boolean) {
        if (this._messages.length == 0) {
            return;
        }
        for (const msg of this._messages) {
            if (!filterFn(msg)) {
                this.alertService.dismiss(msg);
            }
        }
    }

    clear(): void {
        this.alertService.dismissAll();
    }
}
